import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import {
  ProductionWorkOrder,
  ProductionWorkOrderSearchCommand,
  ProductionWorkOrderStatus,
} from '@app/models/production/production-work-order';
import { ProductionWorkOrderProxyService } from '@app/proxies/production/production-work-order.proxy.service';
import { EnumService } from '@app/services/enum.service';

@Component({
  selector: 'app-production-work-orders',
  templateUrl: './production-work-orders.component.html',
  styleUrl: './production-work-orders.component.scss',
})
export class ProductionWorkOrdersComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly proxyService = inject(ProductionWorkOrderProxyService);
  private readonly enumService = inject(EnumService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  productionWorkOrders = signal<ProductionWorkOrder[]>([]);
  readonly filterState = createDynamicFilterState({ storageKey: 'production-work-orders-filters', config: this.filterConfig });
  showFilters = signal(false);

  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  statusOptions: EnumValues = {};
  private filtersPanelInitialized = false;
  private currentSearchCriteria: ProductionWorkOrderSearchCommand = {};

  readonly ProductionWorkOrderStatus = ProductionWorkOrderStatus;

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    this.statusOptions = this.enumService.getEnum('ProductionWorkOrderStatus', 'productionEnums')?.values ?? {};
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngOnInit(): void {}

  redirectToDetails(id?: string): void {
    this.router.navigate(['/production-work-order-details'], { queryParams: { id: id ?? 'new' } });
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    if (!this.statusOptions || Object.keys(this.statusOptions).length === 0) {
      this.statusOptions = this.enumService.getEnum('ProductionWorkOrderStatus', 'productionEnums')?.values ?? {};
    }

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('PWO_Number', 'Number'),
          placeholder: translate('PWO_Enter_Number', 'Enter number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_Status', 'Select Status'),
          options: this.statusOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'plantCode',
          label: translate('PWO_Plant_Code', 'Plant Code'),
          placeholder: translate('PWO_Enter_Plant_Code', 'Enter plant code'),
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const getStatuses = (value: unknown): number[] | undefined => {
      if (!value) return undefined;
      if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'value' in value[0]) {
          return value.map((item: { value: number }) => item.value);
        }
        return value as number[];
      }
      return undefined;
    };

    const searchCriteria: ProductionWorkOrderSearchCommand = {
      number: (filters['number'] as string) || undefined,
      status: getStatuses(filters['statuses']),
      plantCode: (filters['plantCode'] as string) || undefined,
    };

    this.currentSearchCriteria = searchCriteria;
    this.currentPage.set(1);
    this.searchProductionWorkOrders(searchCriteria);
  }

  searchProductionWorkOrders(criteria?: ProductionWorkOrderSearchCommand): void {
    this.startLoading();

    const searchCriteria = criteria || {};

    this.proxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.productionWorkOrders.set(response.data.items);
            this.totalRecords.set(response.data.totalCount);
          } else {
            this.productionWorkOrders.set([]);
            this.totalRecords.set(0);
          }
        },
        error: (error) => {
          console.error('Error fetching production work orders:', error);
          this.productionWorkOrders.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.searchProductionWorkOrders(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.searchProductionWorkOrders(this.currentSearchCriteria);
  }

  getStatusSeverity(status: ProductionWorkOrderStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case ProductionWorkOrderStatus.Approved:
      case ProductionWorkOrderStatus.Completed:
        return 'success';
      case ProductionWorkOrderStatus.InProduction:
        return 'info';
      case ProductionWorkOrderStatus.Draft:
        return 'warn';
      case ProductionWorkOrderStatus.Cancelled:
      case ProductionWorkOrderStatus.Deleted:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getStatusLabel(status: ProductionWorkOrderStatus): string {
    return this.statusOptions[status] ?? `Status ${status}`;
  }
}
